import React, { PropsWithChildren } from 'react';
import { SyntheticEvent, useCallback, useRef } from 'react';

import {
  buttonBaseClasses,
  styled,
  Tab,
  tabClasses,
  Tabs,
  tabsClasses,
  tabScrollButtonClasses,
} from '@mui/material';

import { calcCSSProgressValue } from '../../helper/css-string-helper';
import { EndIcon, StartIcon } from './icons';

export type IconTabsProps = {
  activeIndex: number;
  onChange: (newIndex: number) => void;
  items: {
    name: string;
    icon: React.ReactElement;
  }[];
  className?: string;
};

export const IconTabs = ({
  activeIndex,
  onChange,
  items,
  className,
}: IconTabsProps) => {
  const tabsRef = useRef<HTMLDivElement | null>(null);

  const onTabChange = useCallback(
    (_evt: SyntheticEvent<Element, Event>, newValue: number) => {
      onChange(newValue);
    },
    [onChange],
  );

  return (
    <Container className={className}>
      <StyledTabs
        ref={tabsRef}
        value={activeIndex}
        onChange={onTabChange}
        variant="scrollable"
        scrollButtons="auto"
        slots={{
          StartScrollButtonIcon: StartIcon,
          EndScrollButtonIcon: EndIcon,
        }}
        TabScrollButtonProps={{ disableRipple: true }}
        TabIndicatorProps={{ style: { display: 'none' } }}
      >
        {items.map((item, itemIdx) => (
          <Tab
            key={itemIdx}
            label={item.name}
            disableRipple
            icon={<IconWrapper>{item.icon}</IconWrapper>}
            iconPosition="top"
            component={Comp}
          ></Tab>
        ))}
      </StyledTabs>
    </Container>
  );
};

const Comp = React.forwardRef<HTMLButtonElement, PropsWithChildren>(
  ({ children, ...props }, ref) => {
    return (
      <StyledTab {...props} ref={ref}>
        <ShadowContainer>{children}</ShadowContainer>
      </StyledTab>
    );
  },
);

export default IconTabs;

const Container = styled('div')(({ theme }) => ({
  '--progress': 'var(--sticky-tabs-scroll-progress)',
  '--tabs-height-diff':
    'calc(calc(1 - var(--progress, 0)) * calc(var(--icon-tabs-height) - var(--sticky-tabs-height)))',
  '--real-tabs-height':
    'calc(var(--sticky-tabs-height) + var(--tabs-height-diff))',

  height: 'calc(var(--real-tabs-height) + 10px)',

  [theme.breakpoints.up('sm')]: {
    height: 'calc(var(--real-tabs-height) + 15px)',
  },
}));

const StyledTabs = styled(Tabs)(() => ({ theme }) => ({
  '--background-opacity': 'calc(0.50 * calc(1 - var(--progress, 0)))',

  // calculate the difference of the selected and the unselected tabs during the scroll animation progress
  // selected tabs hight:
  //    bottom: spacer-l -> spacer-m
  //    top: 0px -> spacer-s
  // unselected tabs height:
  //    bottom: spacer-m -> spacer-s
  //    top: 0px -> spacer-xs
  '--height-diff-selected': `calc(
      calc(
          ${calcCSSProgressValue('var(--progress, 0)', 'var(--spacer-l)', 'var(--spacer-m)')} 
        + ${calcCSSProgressValue('var(--progress, 0)', '0px', 'var(--spacer-s)')}
      )
    - calc(
          ${calcCSSProgressValue('var(--progress, 0)', 'var(--spacer-m)', 'var(--spacer-s)')} 
        + ${calcCSSProgressValue('var(--progress, 0)', '0px', 'var(--spacer-xs)')}
      )
  )`,
  '--color-brightness': 'calc(255 * calc(1 - var(--progress, 0)))',
  '--shadow-spacing': '20px',

  paddingTop: 0,
  paddingBottom: 0,

  [`.${tabsClasses.flexContainer}`]: {
    alignItems: 'flex-end',
    justifyContent: 'center',
  },

  [theme.breakpoints.down('sm')]: {
    [`&.${tabsClasses.root}`]: {
      margin: '-15px -20px 0',
    },

    [`.${tabsClasses.flexContainer}`]: {
      padding: '30px 20px 0',
    },
  },

  [`& .${buttonBaseClasses.root}.${tabClasses.root} > div, & .${tabScrollButtonClasses.root}`]:
    {
      height:
        'calc(var(--real-tabs-height) - var(--height-diff-selected) - var(--shadow-spacing))',
      color: 'var(--color-dark-coal)',
    },

  [theme.breakpoints.up('sm')]: {
    '--shadow-spacing': '15px',
    '--color-value': calcCSSProgressValue('var(--progress, 0)', '255', '243'),

    [`&.${tabsClasses.root}`]: {
      marginTop: 'calc(-1 * var(--shadow-spacing))',
    },

    [`.${tabsClasses.scroller}`]: {
      paddingTop: 'calc(2 * var(--shadow-spacing))',
      paddingLeft: 'calc(1 * var(--shadow-spacing))',
      paddingRight: 'calc(1 * var(--shadow-spacing))',
    },

    [`& .${buttonBaseClasses.root}.${tabClasses.root} > div, & .${tabScrollButtonClasses.root}`]:
      {
        color:
          'rgb(var(--color-brightness), var(--color-brightness), var(--color-brightness))',
      },
  },

  [`.${tabScrollButtonClasses.root} ~ .${tabsClasses.scroller} .${tabsClasses.flexContainer}`]:
    { justifyContent: 'unset' },

  [`& .${tabScrollButtonClasses.root}`]: {
    width: 'unset',
    marginTop: '12px',
    alignSelf: 'flex-end',
    backgroundColor: `transparent !important`,

    '&:first-of-type': {
      marginRight: 'var(--spacer-s)',
    },

    '&:last-of-type': {
      marginLeft: 'var(--spacer-s)',
    },
    opacity: 1,

    svg: {
      width: '36px',
      height: '36px',
    },

    [theme.breakpoints.up('md')]: {
      svg: {
        width: '48px',
        height: '48px',
      },
    },
  },
}));

const StyledTab = styled('button')(({ theme }) => ({
  [`&.${buttonBaseClasses.root}.${tabClasses.root}`]: {
    padding: 0,
    overflow: 'initial',
    minHeight: 'unset',
    marginRight: 'var(--spacer-s)',
    backgroundColor: 'transparent',

    '&:last-of-type': {
      marginRight: '-30px',
      paddingRight: '30px',
    },
  },
}));

const ShadowContainer = styled('div')(({ theme }) => ({
  [`.${buttonBaseClasses.root}.${tabClasses.root} &`]: {
    ...theme.typography.footnote,
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    fontWeight: theme.typography.fontWeightMedium,
    textTransform: 'none',
    paddingLeft: 'var(--spacer-xs)',
    paddingRight: 'var(--spacer-xs)',
    minHeight: 'unset',
    minWidth: '120px',
    overflow: 'visible',
    paddingBottom: calcCSSProgressValue(
      'var(--progress, 0)',
      'var(--spacer-m)',
      'var(--spacer-s)',
    ),
    paddingTop: calcCSSProgressValue(
      'var(--progress, 0)',
      '0px',
      'var(--spacer-xs)',
    ),
    backgroundColor: 'var(--color-light-grey)',
    backdropFilter: 'blur(30px)',

    [theme.breakpoints.up('sm')]: {
      backgroundColor: `rgba(var(--color-value), var(--color-value), var(--color-value), ${calcCSSProgressValue('var(--progress, 0)', '0.2', '1.0')} )`,
    },

    whiteSpace: 'nowrap',

    [theme.breakpoints.up('md')]: {
      minWidth: '135px',
    },

    [theme.breakpoints.up('lg')]: {
      minWidth: '150px',
    },
  },

  [`.${buttonBaseClasses.root}.${tabClasses.root}:hover &,
    .${buttonBaseClasses.root}.${tabClasses.root}:focus &,
    .${buttonBaseClasses.root}.${tabClasses.root}.Mui-selected &`]: {
    paddingBottom: 'var(--spacer-l)',
    height: 'calc(var(--real-tabs-height) - var(--shadow-spacing))',
    color: 'var(--color-dark-coal)',
    backgroundColor: 'var(--color-white)',
    boxShadow: '0px 0px 17.6px 2.93px rgba(0, 0, 0, 0.17)',

    [theme.breakpoints.up('sm')]: {
      boxShadow:
        '0px 0px 24px 4px rgba(0, 0, 0, calc(0.17 * var(--progress, 0)))',
      color:
        'rgb(var(--color-brightness), var(--color-brightness), var(--color-brightness))',
      backgroundColor:
        'rgba(255, 255, 255, calc(0.45 + calc(0.55 * var(--progress, 0))))',

      paddingBottom: calcCSSProgressValue(
        'var(--progress, 0)',
        'var(--spacer-l)',
        'var(--spacer-m)',
      ),
      paddingTop: calcCSSProgressValue(
        'var(--progress, 0)',
        '0px',
        'var(--spacer-s)',
      ),
    },
  },
}));

const IconWrapper = styled('div')(({ theme }) => ({
  '--size': '75px',
  width: 'var(--size)',
  height: 'calc(var(--size) * calc(1 - var(--progress, 0)))',
  opacity: 'calc(1 - calc(var(--progress, 0) * 5))',
  transform: 'scaleY(calc(1 - var(--progress, 0)))',
  transformOrigin: 'center top',
  marginBottom: '0',
  transition: 'opacity 0.2s ease',
  // overflow: 'hidden',

  svg: {
    width: 'var(--size)',
    height: 'var(--size)',

    path: {
      fill: 'currentColor',
    },
  },

  [theme.breakpoints.up('md')]: {
    '--size': '90px',
  },

  [theme.breakpoints.up('lg')]: {
    '--size': '100px',
  },
}));
